import { AwsUi } from '../../../../core/runtime/src';
import LegacyClassBuilder from '../../../../core/utils/src/class-builders/legacy';
import { Toggle } from '../../../checkbox/src';
import { FormField } from '../../../form-field/src';
import { arrayItemsEqual, getPreferencesController, getCollectionComponent, renderSafely } from '../utility';
import { fromOptionsGroupsToContentSelection, copyOptionsGroups, compareOptionGroups } from './utils';
var onOptionsChange = function (oldValue, newValue) {
    // If the options are set for the first time (in Angular), it can happen that they are all not visible.
    // In that case both arrays of selected ids are empty, but we still need to notify the table of the change
    // otherwise the table will think that no value is set on the content selection, hence display all columns
    // as opposed to displaying none
    var isSelectionEqual = !(oldValue.length === 0 && newValue.length > 0);
    // Then we check if the selection has actually changed
    isSelectionEqual = isSelectionEqual && compareOptionGroups(oldValue, newValue);
    if (!isSelectionEqual) {
        // https://issues.amazon.com/issues/AWSUI-4103
        // update optionsState only if options are really different
        // in order to not clear user modification before they are applied/discarded
        // when updating state of other parts of the component
        this.__optionsState = copyOptionsGroups(this.options);
        if (!this.__innerChange && this.__parentComponent) {
            // we need to check whether the parent controller has already been retrieved.
            // In React, properties are set before attaching the component,
            // which means that we enter this function before the preferences controller has been set
            this.__updateContentSelection(fromOptionsGroupsToContentSelection(this.options), false);
        }
    }
};
var _a = AwsUi.__addComponents(['awsui-table-content-selector', 'awsui-cards-content-selector'], {
    releaseStatus: 'stable',
    properties: {
        // Displayed on top of the content selection component within preferences.
        title: {
            type: 'string'
        },
        // Array containing categories of content whose display can be toggled by the user.
        // Each entry has the following structure
        // ```
        // {
        //   label: 'displayed as title for the category',
        //   options: [{
        //     id: 'it corresponds to a column id for tables and to a section id for cards',
        //     label: 'Short description of the content',
        //     visible: boolean // Whether the content is visible by default,
        //     editable: boolean // Whether the user is able to toggle its visibility. It defaults to true.
        //   }]
        // }
        // ```
        //
        // If this property is set, the visibility and order of columns/cards sections
        // will be controlled by this component.
        options: {
            type: 'array',
            onChange: onOptionsChange,
            itemTypes: ['TableContentSelector.ContentDescriptionGroup']
        }
    },
    callbacks: {
        attached: function () {
            var _this = this;
            var optionsHaveAlreadyBeenSet = !!this.__optionsState;
            // __optionsState is always bound to the different checkboxes statuses,
            // not necessarily aligned with this.options
            // Needed to keep track of user selection before confirmation
            this.__optionsState = this.__optionsState || [];
            // This flag is used to understand whether the options have been set through user interaction with preferences
            // or by setting the options attribute on the component instance.
            // In the first scenario, preferences must not be updated immediately. They will be bundled with preferences
            // coming from other components and set with one unique call by the preferences controller.
            // In the second scenario, preferences have to be updated immediately
            this.__innerChange = false;
            this.__parentComponent = getCollectionComponent(this.node);
            this.__preferencesController = getPreferencesController(this.node);
            if (optionsHaveAlreadyBeenSet) {
                // We need to make sure that as soon as the preferences controller is attached
                // we apply the content selection options that are specified as attribute
                this.__updateContentSelection(fromOptionsGroupsToContentSelection(this.options));
            }
            this.__preferencesListener = {
                onConfirm: function () { return _this.__onConfirm(); },
                onCancel: function () { return _this.__onCancel(); }
            };
            this.__preferencesController.addListener(this.__preferencesListener);
        },
        detached: function () {
            if (this.__preferencesController) {
                this.__preferencesController.removeListener(this.__preferencesListener);
                // Reset the contentSelection in the table component to its default value, which is undefined,
                // without changing this.options in the table-content-selector component
                this.__updateContentSelection(undefined, false);
            }
        }
    },
    functions: {
        __onConfirm: function () {
            this.__innerChange = true;
            var oldContentSelection = fromOptionsGroupsToContentSelection(this.options);
            var contentSelection = fromOptionsGroupsToContentSelection(this.__optionsState);
            this.options = copyOptionsGroups(this.__optionsState);
            this.__innerChange = false;
            if (!arrayItemsEqual(oldContentSelection, contentSelection)) {
                this.__updateContentSelection(contentSelection, true);
            }
        },
        __onCancel: function () {
            this.__optionsState = copyOptionsGroups(this.options);
            this.__update();
        },
        __updateContentSelection: function (contentSelection, userAction) {
            this.__parentComponent.__controller.setContentSelection(contentSelection, userAction);
        }
    },
    render: renderSafely(function (m, h) {
        var _this = this;
        var options = this.__optionsState || [];
        var getComponentClassName = function (suffix) {
            return new LegacyClassBuilder(_this).addComponentClass(suffix).toClassName();
        };
        var toggleOptionVisibility = function (option) { return function () {
            option.visible = !option.visible;
            _this.__update();
        }; };
        var displayOption = function (option) {
            var cb = new LegacyClassBuilder(_this).addComponentClass('option');
            return (AwsUi.__createElement("div", { className: cb.toClassName() },
                AwsUi.__createElement("span", { className: getComponentClassName('option-label') }, option.label),
                option.editable === false ? ('') : (AwsUi.__createElement(Toggle, { className: getComponentClassName('toggle'), checked: Boolean(option.visible), change: toggleOptionVisibility(option), ariaLabel: option.label }))));
        };
        var displayOptionGroup = function (group) {
            return (AwsUi.__createElement("div", { className: getComponentClassName('options-group') },
                AwsUi.__createElement("div", { className: getComponentClassName('options-group-label') }, group.label),
                group.options.map(displayOption)));
        };
        return AwsUi.__createElement(FormField, { label: this.title, control: options.map(displayOptionGroup), stretch: true });
    })
}), TableContentSelector = _a[0], CardsContentSelector = _a[1];
export { TableContentSelector, CardsContentSelector };
//# sourceMappingURL=index.js.map